import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import api from "../lib/api";
import { DatePicker } from "./DateTimePicker";

export default function QuoteForm({
  serviceType = "other",
  title = "Request a Quote",
  eyebrow = "Get In Touch",
  showEventFields = true,
  showGuestCount = true,
  showLocation = true,
}) {
  const [form, setForm] = useState({
    service_type: serviceType,
    full_name: "",
    email: "",
    phone: "",
    event_date: "",
    event_location: "",
    guest_count: "",
    details: "",
  });
  const [loading, setLoading] = useState(false);
  const onChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, guest_count: Number(form.guest_count) || 0 };
      const { data } = await api.post("/quotes", payload);
      toast.success(data.message || "Quote received. We'll respond within 1 business hour.");
      setForm({ ...form, full_name: "", email: "", phone: "", event_date: "", event_location: "", guest_count: "", details: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.toString?.() || "Could not submit. Please call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-[#0c0c0c] border border-white/10 p-8 md:p-10" data-testid="quote-form">
      <div className="label-eyebrow mb-2">{eyebrow}</div>
      <h3 className="font-display text-2xl md:text-3xl text-white mb-6">{title}</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">Full Name</label>
          <input className="lux-input" required value={form.full_name} onChange={(e) => onChange("full_name", e.target.value)} data-testid="quote-name" />
        </div>
        <div>
          <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">Phone</label>
          <input className="lux-input" required value={form.phone} onChange={(e) => onChange("phone", e.target.value)} data-testid="quote-phone" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">Email</label>
          <input type="email" className="lux-input" required value={form.email} onChange={(e) => onChange("email", e.target.value)} data-testid="quote-email" />
        </div>

        {showEventFields && (
          <>
            <div className={showGuestCount ? "" : "md:col-span-2"}>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">Event Date</label>
              <DatePicker
                value={form.event_date}
                onChange={(v) => onChange("event_date", v)}
                testId="quote-date"
                placeholder="Select event date"
              />
            </div>
            {showGuestCount && (
              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">Approx. Guests</label>
                <input
                  type="number"
                  min="1"
                  className="lux-input"
                  placeholder="e.g., 50"
                  value={form.guest_count}
                  onChange={(e) => onChange("guest_count", e.target.value)}
                  data-testid="quote-guests"
                />
              </div>
            )}
            {showLocation && (
              <div className="md:col-span-2">
                <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">Event Location</label>
                <input
                  className="lux-input"
                  placeholder="Venue, city, or address"
                  value={form.event_location}
                  onChange={(e) => onChange("event_location", e.target.value)}
                  data-testid="quote-location"
                />
              </div>
            )}
          </>
        )}

        <div className="md:col-span-2">
          <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-1.5">Tell us more</label>
          <textarea
            rows={3}
            className="lux-input"
            placeholder="A few details about your event or service needs…"
            value={form.details}
            onChange={(e) => onChange("details", e.target.value)}
            data-testid="quote-details"
          />
        </div>
      </div>

      <button type="submit" disabled={loading} className="btn-accent mt-6 w-full justify-center" data-testid="quote-submit">
        {loading ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
        {loading ? "Sending…" : "Request Quote"}
      </button>
    </form>
  );
}
